// A request waiting for confirmation, in the person's words
// (02-DESIGN/03-console/01-the-console.md § the words: an escalation is *a
// request waiting for you*, resolving it *Approve* · *Deny*; 03-the-guardrails-
// service.md § the console: what was asked, as the subject in the design's
// words and the snapshot fields the rule named; who asked, when, by which
// rule). The wire keeps its words; the page shows these.

import { subject } from "@impire-io/sdk";

import type { Tone } from "../../ui/components.tsx";
import { Kind, Status, type Principal } from "../../wire/guard.ts";

/** describeSubject says what was asked, from the subject it was asked on: the address after the root; a subject not under it, or not a workload's, is shown as it is. */
export function describeSubject(subj: string): string {
  let toks: string[];
  try {
    toks = subject.address(subj);
  } catch {
    return subj;
  }
  const [family, id, kind, ...rest] = toks;
  if (family !== subject.Family.workload || id === undefined || kind === undefined) {
    return subj;
  }
  const name = rest.join(".");
  if (kind === subject.TOKEN_TOOL && name !== "") {
    return `Call ${name} on ${id}`;
  }
  if (kind === subject.TOKEN_INFER && name !== "") {
    return `A ${name} request to ${id}`;
  }
  const verb = [kind, ...rest].join(".");
  return `${verb} on ${id}`;
}

/** principalWords says who asked: you, a person by name, an app, a workload. */
export function principalWords(p: Principal, me: string): string {
  if (me !== "" && p.token === me) {
    return "you";
  }
  const display = p.display !== undefined && p.display !== "" ? p.display : p.token;
  switch (p.kind) {
    case Kind.app:
      return `the app ${display}`;
    case Kind.workload:
      return `the workload ${display}`;
    default:
      return display;
  }
}

/** ContextRow is one snapshot field the rule named, flattened for a person to read. */
export interface ContextRow {
  key: string;
  value: string;
}

/** contextRows flattens the snapshot's fields, one row per leaf, objects and lists as JSON. */
export function contextRows(ctx: Record<string, unknown> | undefined): ContextRow[] {
  if (ctx === undefined) {
    return [];
  }
  const out: ContextRow[] = [];
  const walk = (prefix: string, v: unknown) => {
    if (typeof v === "object" && v !== null && !Array.isArray(v)) {
      const entries = Object.entries(v as Record<string, unknown>);
      if (entries.length === 0) {
        out.push({ key: prefix, value: "{}" });
        return;
      }
      for (const [k, val] of entries) {
        walk(prefix === "" ? k : `${prefix}.${k}`, val);
      }
      return;
    }
    out.push({ key: prefix, value: typeof v === "string" ? v : JSON.stringify(v) });
  };
  for (const [k, v] of Object.entries(ctx)) {
    walk(k, v);
  }
  return out;
}

/** statusWords is where a request stands, as the page says it. */
export function statusWords(status: string): { word: string; tone: Tone } {
  switch (status) {
    case Status.open:
      return { word: "waiting for you", tone: "info" };
    case Status.granted:
      return { word: "approved", tone: "ok" };
    case Status.denied:
      return { word: "denied", tone: "bad" };
    case Status.expired:
      return { word: "expired", tone: "neutral" };
    case Status.revoked:
      return { word: "withdrawn", tone: "neutral" };
    default:
      return { word: status, tone: "neutral" };
  }
}

/** APPROVE_CONSEQUENCE is what approving binds (02-escalation-and-grants.md § the retry redeems a deposit). */
export const APPROVE_CONSEQUENCE = "The next identical request will go through once.";

/** APPROVE_AND_SEND_CONSEQUENCE is what approving binds where the element asks again itself: the identical request, at once. */
export const APPROVE_AND_SEND_CONSEQUENCE = "It is sent again now, exactly as before, and goes through once.";

/** DENY_CONSEQUENCE is what denying does. */
export const DENY_CONSEQUENCE = "Whoever is waiting is told to stop. They may ask again.";
